import { Fragment, memo, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cc from 'classcat';
import { EIconSize } from 'utils/constants/app/ui';
import { mountPoint } from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import { GlobalIcon } from 'components/GlobalIcon';
import { useCustomHistory } from 'newhooks/generic/useCustomHistory';
import './index.less';

export type TBreadcrumbItemType = {
  title: ReactNode;
  id?: number;
  onClick?: () => void;
  active?: boolean;
};

type TAdvancedHeaderBreadcrumbProps = {
  additionalNavigationItems?: TBreadcrumbItemType[];
};

export const AdvancedHeaderBreadcrumb = memo(
  ({ additionalNavigationItems = [] }: TAdvancedHeaderBreadcrumbProps) => {
    const { history, goBack } = useCustomHistory();
    const { t } = useTranslation();

    const navigationItems: TBreadcrumbItemType[] = useMemo(() => {
      const mainItems = [
        {
          id: 0,
          title: t('sportsbook.home'),
          onClick: () => {
            const redirectPath = `${mountPoint(window.getPathname())}`;

            history.push(redirectPath);
          }
        }
      ];

      return [...mainItems, ...additionalNavigationItems];
    }, [additionalNavigationItems]);

    const handleBackClick = useCallback(() => {
      const fallbackUrl = `${mountPoint(window.getPathname())}`;
      goBack(fallbackUrl);
    }, [history]);

    return (
      <div
        className={cc([
          'advancedHeader',
          { 'advancedHeader--mobile': isMobile() }
        ])}
      >
        <GlobalIcon
          onClick={handleBackClick}
          color="var(--v3-black-45)"
          lib="generic"
          name="arrowLeft"
          theme="default"
          size={EIconSize._12}
          style={{ width: EIconSize._12, height: EIconSize._12 }}
          className="advancedHeader__backIcon"
        />
        <div className="advancedHeader__breadcrumb">
          {navigationItems.map((item, index) => (
            <Fragment key={item.id ?? index}>
              <div
                className={cc([
                  'advancedHeader__breadcrumb__item',
                  {
                    'advancedHeader__breadcrumb__item--active': item.active,
                    'advancedHeader__breadcrumb__item--clickable':
                      !!item.onClick
                  }
                ])}
                onClick={item.onClick}
              >
                {item.title}
              </div>
              {index !== navigationItems.length - 1 ? (
                <GlobalIcon
                  className="advancedHeader__breadcrumb__arrow"
                  color="var(--v3-black-45)"
                  lib="generic"
                  name="caretDownSmall"
                  theme="default"
                  size={24}
                />
              ) : null}
            </Fragment>
          ))}
        </div>
      </div>
    );
  }
);
